import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import CircumplexDiagram from "../components/CircumplexDiagram";
import EmployeeDrawer from "../components/EmployeeDrawer";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronRight, ZoomOut, Sparkles, Pencil, Trash2, Target, Download, AlertTriangle, Grid2x2 } from "lucide-react";
import { downloadSvgAsPng } from "../lib/exportPng";
import { transferTargets } from "../lib/connections";
import { ReceivedWorkProducts } from "../components/ReceivedWorkProducts";
import GoalsSection from "../components/GoalsSection";
import SwotDialog from "../components/SwotDialog";

const LEVEL_PRESETS = ["Werkmaatschappij", "Afdeling", "Team", "Individuele medewerker", "Systeem I"];

export default function ModelerPage() {
  const { user } = useAuth();
  const canEdit = ["superadmin", "consultant", "org_admin"].includes(user.role);

  const [params] = useSearchParams();
  const initialNode = useRef(params.get("node"));
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setCurrentOrgId] = useState(params.get("org"));
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [orgEmployees, setOrgEmployees] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [strategies, setStrategies] = useState([]);

  const [sys1Start, setSys1Start] = useState(0);
  const [sys2Index, setSys2Index] = useState(0);
  const [sys4Index, setSys4Index] = useState(0);

  const [drawer, setDrawer] = useState({ open: false, systemKey: "s5", unitId: null });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", level_name: "Team", is_individual: false, emp_name: "", emp_role: "", emp_email: "", emp_notes: "" });
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm, setRenameForm] = useState({ name: "", level_name: "" });

  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [analyzeText, setAnalyzeText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [goalsChildId, setGoalsChildId] = useState(null);
  const [swotOpen, setSwotOpen] = useState(false);

  const recentKey = `vsm-recent-orgs-${user.id}`;

  // ---- data loading ----
  useEffect(() => {
    api.get("/organizations").then(({ data }) => {
      const recent = JSON.parse(localStorage.getItem(recentKey) || "[]");
      const rank = (o) => (recent.includes(o.id) ? recent.indexOf(o.id) : recent.length);
      const sorted = [...data].sort((a, b) => rank(a) - rank(b));
      setOrgs(sorted);
      if (sorted.length) setCurrentOrgId((prev) => prev || sorted[0].id);
    });
  }, [recentKey]);

  useEffect(() => {
    if (!currentOrgId) return;
    const recent = JSON.parse(localStorage.getItem(recentKey) || "[]").filter((id) => id !== currentOrgId);
    localStorage.setItem(recentKey, JSON.stringify([currentOrgId, ...recent].slice(0, 50)));
  }, [currentOrgId, recentKey]);

  useEffect(() => {
    if (!currentOrgId) return;
    if (initialNode.current) {
      setCurrentNodeId(initialNode.current);
      initialNode.current = null;
    } else api.get(`/organizations/${currentOrgId}`).then(({ data }) => setCurrentNodeId(data.default_node_id));
    api.get(`/organizations/${currentOrgId}/strategies`).then(({ data }) => setStrategies(data));
    api.get(`/organizations/${currentOrgId}/employees`).then(({ data }) => {
      setOrgEmployees(data);
      if (user.employee_id) setMyEmployee(data.find((e) => e.id === user.employee_id) || null);
    });
  }, [currentOrgId, user.employee_id]);

  const loadNode = useCallback((nodeId) => {
    if (!nodeId) return;
    api.get(`/nodes/${nodeId}`).then(({ data }) => setDetail(data));
  }, []);

  const [transfers, setTransfers] = useState([]);
  const reloadTransfers = useCallback(() => {
    if (currentNodeId) api.get(`/nodes/${currentNodeId}/transfers`).then(({ data }) => setTransfers(data));
  }, [currentNodeId]);

  useEffect(() => {
    reloadTransfers();
  }, [reloadTransfers]);

  useEffect(() => {
    loadNode(currentNodeId);
  }, [currentNodeId, loadNode]);

  const refreshNode = () => loadNode(currentNodeId);
  const refreshEmployees = () =>
    currentOrgId && api.get(`/organizations/${currentOrgId}/employees`).then(({ data }) => setOrgEmployees(data));

  const goToNode = (nodeId) => {
    setSys1Start(0);
    setSys2Index(0);
    setSys4Index(0);
    setCurrentNodeId(nodeId);
  };

  const openSystem = (systemKey, unitId) => setDrawer({ open: true, systemKey, unitId });

  // ---- mutations ----
  const addSystem1 = async () => {
    if (!addForm.name.trim()) return toast.error("Naam is verplicht");
    const body = { name: addForm.name, level_name: addForm.level_name, is_individual: addForm.is_individual };
    if (addForm.is_individual && addForm.emp_name.trim())
      body.new_employee = { name: addForm.emp_name, role: addForm.emp_role, email: addForm.emp_email, notes: addForm.emp_notes };
    const { data } = await api.post(`/nodes/${currentNodeId}/system1`, body);
    setDetail(data);
    setAddOpen(false);
    setAddForm({ name: "", level_name: "Team", is_individual: false, emp_name: "", emp_role: "", emp_email: "", emp_notes: "" });
    refreshEmployees();
    toast.success("Systeem I toegevoegd");
  };

  const saveRename = async () => {
    await api.patch(`/nodes/${currentNodeId}`, renameForm);
    setRenameOpen(false);
    refreshNode();
    api.get("/organizations").then(({ data }) => setOrgs(data));
    toast.success("Opgeslagen");
  };

  const deleteChild = async (child) => {
    if (!window.confirm(`"${child.name}" en alle onderliggende niveaus verwijderen?`)) return;
    await api.delete(`/nodes/${child.id}`);
    refreshNode();
    toast.success("Verwijderd");
  };

  const runAnalyze = async () => {
    setAnalyzeOpen(true);
    setAnalyzeText("");
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/nodes/${currentNodeId}/analyze`, { method: "POST", credentials: "include" });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAnalyzeText((t) => t + dec.decode(value, { stream: true }));
      }
    } catch (e) {
      setAnalyzeText("AI-analyse niet beschikbaar.");
    }
    setAnalyzing(false);
  };

  if (!detail) {
    return (
      <div className="min-h-screen bg-white">
        <Header orgs={orgs} currentOrgId={currentOrgId} onSelectOrg={setCurrentOrgId} myEmployee={myEmployee} onProfileSaved={refreshEmployees} />
        <div className="flex items-center justify-center h-[70vh] text-zinc-400 text-sm tracking-widest uppercase">
          {orgs.length === 0 ? "Nog geen organisaties. Maak er een aan via Beheer." : "Model laden…"}
        </div>
      </div>
    );
  }

  const node = detail.node;
  const ancestors = detail.ancestors;

  const myEmpId = user.employee_id;
  const inS5 = node.system5_employees.some((e) => e.id === myEmpId);
  const inS3 = node.system3_employees.some((e) => e.id === myEmpId);
  const canGoalS3 = canEdit || inS5;
  const canGoalOps = canEdit || inS3;
  const goalsChild = goalsChildId ? detail.system1_children.find((c) => c.id === goalsChildId) : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header orgs={orgs} currentOrgId={currentOrgId} currentNodeId={currentNodeId} onSelectOrg={setCurrentOrgId} myEmployee={myEmployee} onProfileSaved={refreshEmployees} />

      {/* breadcrumb / level bar */}
      <div className="border-b border-zinc-200 bg-zinc-50 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="recursion-breadcrumb">
          {ancestors.map((a, i) => (
            <span key={a.id} className="flex items-center gap-1">
              <button
                className="hover:underline text-zinc-500"
                onClick={() => goToNode(a.id)}
                data-testid={`recursion-breadcrumb-item-${i}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 mr-1">{a.level_name}</span>
                {a.name}
              </button>
              <ChevronRight className="h-3.5 w-3.5 text-zinc-300" />
            </span>
          ))}
          <span className="flex items-center gap-1 font-semibold">
            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 mr-1">{node.level_name}</span>
            {node.name}
          </span>
          {canEdit && !node.protected && (
            <Button size="icon" variant="ghost" className="h-6 w-6 ml-1" onClick={() => { setRenameForm({ name: node.name, level_name: node.level_name }); setRenameOpen(true); }} data-testid="rename-node-button">
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-none h-9"
            disabled={!node.parent_id}
            onClick={() => node.parent_id && goToNode(node.parent_id)}
            data-testid="zoom-out-button"
          >
            <ZoomOut className="h-4 w-4 mr-2" /> Uitzoomen
          </Button>
          <Button
            variant="outline"
            className="rounded-none h-9"
            onClick={() => {
              const svg = document.querySelector('[data-testid="circumplex-diagram-svg"]');
              const safe = node.name.replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-") || "model";
              downloadSvgAsPng(svg, `${node.level_name}: ${node.name}`, `circumplex-vsm-${safe}.png`).catch(() => toast.error("Downloaden mislukt"));
            }}
            data-testid="download-png-button"
          >
            <Download className="h-4 w-4 mr-2" /> Download PNG
          </Button>
          <Button variant="outline" className="rounded-none h-9" onClick={runAnalyze} data-testid="ai-analyze-button">
            <Sparkles className="h-4 w-4 mr-2" /> AI-analyse
          </Button>
          <Button variant="outline" className="rounded-none h-9" onClick={() => setSwotOpen(true)} data-testid="ai-swot-button">
            <Grid2x2 className="h-4 w-4 mr-2" /> AI SWOT
          </Button>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_320px]">
        {/* diagram */}
        <div className="p-4 md:p-8 flex items-start justify-center overflow-hidden">
          <div className="w-full max-w-3xl">
            <CircumplexDiagram
              detail={detail}
              canEdit={canEdit}
              sys1Start={sys1Start}
              onSys1Prev={() => setSys1Start((s) => Math.max(0, s - 1))}
              onSys1Next={() => setSys1Start((s) => Math.min(detail.system1_children.length - 3, s + 1))}
              onZoomSys1={(c) => (c.is_individual ? openSystem("s5", null) : goToNode(c.id))}
              sys2Index={sys2Index}
              onSys2Prev={() => setSys2Index((i) => (i - 1 + node.system2_units.length) % node.system2_units.length)}
              onSys2Next={() => setSys2Index((i) => (i + 1) % node.system2_units.length)}
              sys4Index={sys4Index}
              onSys4Prev={() => setSys4Index((i) => (i - 1 + node.system4_units.length) % node.system4_units.length)}
              onSys4Next={() => setSys4Index((i) => (i + 1) % node.system4_units.length)}
              onOpenSystem={openSystem}
              onAddSys1={() => setAddOpen(true)}
            />
          </div>
        </div>

        {/* side panel: System I list */}
        <aside className="border-l border-zinc-200 p-5 bg-zinc-50/50">
          {node.goals?.length > 0 && (
            <div className="mb-5 border-l-2 border-zinc-900 bg-white p-3" data-testid="current-node-goals">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-2">
                <Target className="h-3.5 w-3.5" /> Doelen voor dit Systeem I
              </div>
              {node.goals.map((g) => (
                <div key={g.id} className="text-sm mb-1">
                  • {g.text}
                  {g.parent_goal_text && <span className="block text-[10px] font-mono text-zinc-400 ml-3">↳ hoofddoel: {g.parent_goal_text}</span>}
                </div>
              ))}
            </div>
          )}
          <h3 className="font-grotesk font-semibold text-lg tracking-tight mb-1">Systemen I</h3>
          <p className="text-xs text-zinc-500 mb-4">De operationele eenheden. Klik om in te zoomen (recursie).</p>
          <div className="space-y-2" data-testid="system1-list">
            {detail.system1_children.map((c, i) => {
              const gap = c.skill_gap || [];
              return (
              <div key={c.id} className={`border p-3 flex items-center justify-between gap-2 ${gap.length ? "border-red-600 bg-red-50" : "border-zinc-300 bg-white"}`} data-testid={`system1-list-item-${i}`} data-alert={gap.length > 0}>
                <button className="text-left min-w-0" onClick={() => (c.is_individual ? null : goToNode(c.id))}>
                  <div className={`font-grotesk font-medium truncate ${gap.length ? "text-red-700" : ""}`}>{c.employee ? c.employee.name : c.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                    {c.level_name} {c.is_individual ? "· medewerker" : `· ${c.child_count} sub`}
                    {c.goals?.length ? ` · ${c.goals.length} doel` : ""}
                  </div>
                  {gap.length > 0 && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] text-red-700" title={gap.join("\n")} data-testid={`system1-list-gap-${i}`}>
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{gap.length} {gap.length === 1 ? "vaardigheid ontbreekt" : "vaardigheden ontbreken"}: {gap.join("; ")}</span>
                    </div>
                  )}
                </button>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setGoalsChildId(c.id)} data-testid={`goals-sys1-${i}`} title="Doelen">
                    <Target className="h-4 w-4" />
                  </Button>
                  {!c.is_individual && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => goToNode(c.id)} data-testid={`zoom-in-sys1-${i}`} title="Inzoomen">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteChild(c)} data-testid={`delete-sys1-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
            {detail.system1_children.length === 0 && <p className="text-sm text-zinc-400">Nog geen Systemen I.</p>}
          </div>
          {canEdit && !node.is_individual && (
            <Button className="w-full rounded-none bg-zinc-900 hover:bg-zinc-700 mt-4" onClick={() => setAddOpen(true)} data-testid="add-sys1-panel-button">
              + Systeem I toevoegen
            </Button>
          )}
        </aside>
      </div>

      {/* Employee drawer */}
      <EmployeeDrawer
        open={drawer.open}
        onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
        systemKey={drawer.systemKey}
        unitId={drawer.unitId}
        node={node}
        orgId={currentOrgId}
        canEdit={canEdit}
        canGoalS3={canGoalS3}
        canGoalOps={canGoalOps}
        orgEmployees={orgEmployees}
        onRefresh={refreshNode}
        refreshEmployees={refreshEmployees}
        strategies={strategies}
        myEmployeeId={user.employee_id}
        transfers={transfers}
        reloadTransfers={reloadTransfers}
      />

      {/* Add System I dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-sys1-dialog">
          <DialogHeader>
            <DialogTitle className="font-grotesk">Nieuw Systeem I</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Naam</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="sys1-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Recursieniveau (label)</Label>
              <Select value={addForm.level_name} onValueChange={(v) => setAddForm({ ...addForm, level_name: v })}>
                <SelectTrigger className="rounded-none border-zinc-900 mt-1" data-testid="sys1-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL_PRESETS.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border border-zinc-200 p-3">
              <div>
                <div className="text-sm font-medium">Individuele medewerker (laagste niveau)</div>
                <div className="text-xs text-zinc-500">Dit Systeem I is een persoon, geen verder recursieniveau.</div>
              </div>
              <Switch checked={addForm.is_individual} onCheckedChange={(v) => setAddForm({ ...addForm, is_individual: v })} data-testid="sys1-individual-switch" />
            </div>
            {addForm.is_individual && (
              <div className="space-y-2 border border-zinc-900 p-3 bg-zinc-50">
                <Input placeholder="Naam medewerker" value={addForm.emp_name} onChange={(e) => setAddForm({ ...addForm, emp_name: e.target.value })} className="rounded-none border-zinc-400" data-testid="sys1-emp-name-input" />
                <Input placeholder="Rol / functie" value={addForm.emp_role} onChange={(e) => setAddForm({ ...addForm, emp_role: e.target.value })} className="rounded-none border-zinc-400" />
                <Input placeholder="E-mail" value={addForm.emp_email} onChange={(e) => setAddForm({ ...addForm, emp_email: e.target.value })} className="rounded-none border-zinc-400" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={addSystem1} data-testid="confirm-add-sys1-button">
              Toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename node dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent data-testid="rename-node-dialog">
          <DialogHeader>
            <DialogTitle className="font-grotesk">Niveau bewerken</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Naam</Label>
              <Input value={renameForm.name} onChange={(e) => setRenameForm({ ...renameForm, name: e.target.value })} className="rounded-none border-zinc-900 mt-1" data-testid="rename-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Recursieniveau (label)</Label>
              <Input value={renameForm.level_name} onChange={(e) => setRenameForm({ ...renameForm, level_name: e.target.value })} className="rounded-none border-zinc-900 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={saveRename} data-testid="save-rename-button">
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* System I goals dialog */}
      <Dialog open={!!goalsChildId} onOpenChange={(o) => !o && setGoalsChildId(null)}>
        <DialogContent data-testid="sys1-goals-dialog">
          <DialogHeader>
            <DialogTitle className="font-grotesk flex items-center gap-2">
              <Target className="h-4 w-4" /> Doelen · {goalsChild?.employee ? goalsChild.employee.name : goalsChild?.name}
            </DialogTitle>
          </DialogHeader>
          <GoalsSection
            title="Subdoelen"
            subtitle="Gesteld door Systeem III · gekoppeld aan een hoofddoel"
            goals={goalsChild?.goals}
            canGive={canGoalOps}
            requiresParent
            parentOptions={node.system3_goals}
            testid="sys1-goals-section"
            onAdd={async (t, pid) => {
              const { data } = await api.post(`/nodes/${goalsChildId}/goals`, { text: t, parent_goal_id: pid });
              setDetail(data);
            }}
            onDelete={async (id) => {
              const { data } = await api.delete(`/nodes/${goalsChildId}/goals/${id}`);
              setDetail(data);
            }}
            onEdit={async (id, t, pid) => {
              const { data } = await api.patch(`/nodes/${goalsChildId}/goals/${id}`, { text: t, parent_goal_id: pid });
              setDetail(data);
            }}
            strategies={strategies}
            canLink={canGoalOps || (goalsChild?.member_ids || []).includes(user.employee_id)}
            canLoadTaker={canEdit || (goalsChild?.member_ids || []).includes(user.employee_id)}
            onLoad={async (gid, set, v) => {
              const { data } = await api.put(`/nodes/${goalsChildId}/goals/${gid}/load`, { set, ...v });
              setDetail(data);
            }}
            memberSkills={new Set(orgEmployees.filter((e) => (goalsChild?.member_ids || []).includes(e.id)).flatMap((e) => e.skill_ids || []))}
            onLinkStrategies={async (gid, ids) => {
              const { data } = await api.put(`/nodes/${goalsChildId}/goals/${gid}/strategies`, { strategy_ids: ids });
              setDetail(data);
            }}
            onProgress={async (gid, wpId, pct, ok) => {
              const { data } = await api.put(`/nodes/${goalsChildId}/goals/${gid}/progress`, { work_product_id: wpId, percent: pct, approved: ok });
              setDetail(data);
            }}
            transferCtx={goalsChild ? {
              source: { node_id: node.id, system: "s1", child_id: goalsChild.id },
              targets: transferTargets(node, "s1", goalsChild),
              transfers,
              reload: reloadTransfers,
              refreshGoals: () => loadNode(currentNodeId),
            } : null}
          />
          <ReceivedWorkProducts
            transfers={transfers.filter((t) => t.to.node_id === node.id && t.to.child_id === goalsChildId)}
            canReview={canGoalOps || (goalsChild?.member_ids || []).includes(user.employee_id)}
            reload={reloadTransfers}
          />
        </DialogContent>
      </Dialog>

      <SwotDialog open={swotOpen} onOpenChange={setSwotOpen} orgId={currentOrgId} canEdit={canEdit} />

      {/* AI analyze dialog */}
      <Dialog open={analyzeOpen} onOpenChange={setAnalyzeOpen}>
        <DialogContent className="max-w-lg" data-testid="analyze-dialog">
          <DialogHeader>
            <DialogTitle className="font-grotesk flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> AI-analyse · Claude Sonnet 5.5
            </DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-zinc-700 max-h-[60vh] overflow-y-auto font-mono leading-relaxed" data-testid="analyze-output">
            {analyzeText || (analyzing ? "Analyse wordt gegenereerd…" : "")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
